import {useCallback, useMemo, useRef, useState} from "react";
import Utils from "../../../../../../core/services/utils/utils";
import {
    allocationSignature,
    PLANNING_DRAFT_HISTORY_CAP,
    PlanningDraftChange,
    PlanningDraftChangeKind,
    PlanningDraftHistoryEntry,
    PlanningDraftRow,
    reattachAllocations,
    serverSignature,
} from "./planning-draft";

export interface UsePlanningDraftResult<T extends PlanningDraftRow> {
    canUndo: boolean;
    canRedo: boolean;
    changes: PlanningDraftChange[];
    isServerDirty: boolean;
    isAllocationDirty: boolean;
    commit: (previousItems: T[], change: { kind: PlanningDraftChangeKind; label: string; segments?: PlanningDraftChange['segments']; orderIndex?: number; partNumber?: string }) => void;
    dismissCommittedDetails: (saved: Array<{ orderIndex: number; partNumber: string }>) => void;
    undo: (currentItems: T[]) => T[] | null;
    redo: (currentItems: T[]) => T[] | null;
    revert: (currentItems: T[]) => T[] | null;
    reset: (items: T[]) => void;
    clearHistory: () => void;
    setServerBaseline: (items: T[]) => void;
    readBaseline: () => T[];
    acknowledgeSavedDetails: (saved: Array<{ orderIndex: number; partNumber: string; qty: number; note: string }>) => void;
}

/**
 * Tracks undo, redo, and the last saved purchase-plan snapshot.
 * Vendor allocations stay in the working copy and are not part of the saved baseline.
 * @param items     The working rows. Used to decide whether save, revert, or leaving the page should warn.
 */
export const usePlanningDraft = <T extends PlanningDraftRow>(items: T[]): UsePlanningDraftResult<T> => {
    const pastRef = useRef<PlanningDraftHistoryEntry<T>[]>([]);
    const futureRef = useRef<PlanningDraftHistoryEntry<T>[]>([]);
    const baselineRowsRef = useRef<T[]>([]);
    const baselineSignatureRef = useRef(serverSignature([]));
    const loadedAllocationSignatureRef = useRef(allocationSignature([]));
    const [version, setVersion] = useState(0);

    const bump = useCallback(() => setVersion(current => current + 1), []);

    /**
     * Records the rows from before a change so it can be undone.
     */
    const commit = useCallback((previousItems: T[], change: { kind: PlanningDraftChangeKind; label: string; segments?: PlanningDraftChange['segments']; orderIndex?: number; partNumber?: string }) => {
        const entry: PlanningDraftHistoryEntry<T> = {
            change: {
                id: Utils.createUUID(),
                kind: change.kind,
                label: change.label,
                at: Date.now(),
                segments: change.segments,
                orderIndex: change.orderIndex,
                partNumber: change.partNumber,
            },
            snapshot: Utils.deepCopy(previousItems),
        };
        pastRef.current = [...pastRef.current, entry].slice(-PLANNING_DRAFT_HISTORY_CAP);
        futureRef.current = [];
        bump();
    }, [bump]);

    /**
     * Restores the previous snapshot and keeps the current rows for redo.
     */
    const undo = useCallback((currentItems: T[]) => {
        const entry = pastRef.current[pastRef.current.length - 1];
        if (!entry)
            return null;
        pastRef.current = pastRef.current.slice(0, -1);
        futureRef.current = [...futureRef.current, {change: entry.change, snapshot: Utils.deepCopy(currentItems)}];
        bump();
        return Utils.deepCopy(entry.snapshot);
    }, [bump]);

    /**
     * Reapplies the most recently undone change.
     */
    const redo = useCallback((currentItems: T[]) => {
        const entry = futureRef.current[futureRef.current.length - 1];
        if (!entry)
            return null;
        futureRef.current = futureRef.current.slice(0, -1);
        pastRef.current = [...pastRef.current, {change: entry.change, snapshot: Utils.deepCopy(currentItems)}].slice(-PLANNING_DRAFT_HISTORY_CAP);
        bump();
        return Utils.deepCopy(entry.snapshot);
    }, [bump]);

    /**
     * Drops undo and redo history without changing the saved baseline.
     */
    const clearHistory = useCallback(() => {
        pastRef.current = [];
        futureRef.current = [];
        bump();
    }, [bump]);

    /**
     * Stores the last loaded or saved server rows. Allocations on those rows are not treated as saved.
     */
    const setServerBaseline = useCallback((nextItems: T[]) => {
        baselineRowsRef.current = Utils.deepCopy(nextItems);
        baselineSignatureRef.current = serverSignature(nextItems);
        bump();
    }, [bump]);

    /**
     * Returns a copy of the last loaded or saved server rows.
     */
    const readBaseline = useCallback(() => Utils.deepCopy(baselineRowsRef.current), []);

    /**
     * Removes quantity and note history for rows whose detail update has already been stored.
     */
    const dismissCommittedDetails = useCallback((saved: Array<{ orderIndex: number; partNumber: string }>) => {
        if (!saved.length)
            return;
        const committed = (change: PlanningDraftChange) => (
            (change.kind === 'updateQty' || change.kind === 'updateNote')
            && saved.some(item => item.orderIndex === change.orderIndex && item.partNumber === change.partNumber)
        );
        const past = pastRef.current.filter(entry => !committed(entry.change));
        const future = futureRef.current.filter(entry => !committed(entry.change));
        if (past.length === pastRef.current.length && future.length === futureRef.current.length)
            return;
        pastRef.current = past;
        futureRef.current = future;
        bump();
    }, [bump]);

    /**
     * Marks quantity and note updates as saved without treating other edits as saved.
     */
    const acknowledgeSavedDetails = useCallback((saved: Array<{ orderIndex: number; partNumber: string; qty: number; note: string }>) => {
        if (!saved.length)
            return;
        baselineRowsRef.current = baselineRowsRef.current.map(row => {
            const match = saved.find(item => item.orderIndex === (row.orderIndex ?? 0) && item.partNumber === row.partNumber);
            if (!match)
                return row;
            return {...row, qty: match.qty, note: match.note};
        });
        baselineSignatureRef.current = serverSignature(baselineRowsRef.current);
        bump();
    }, [bump]);

    /**
     * Replaces history and both baselines. Used when a plan is loaded or a new session starts.
     */
    const reset = useCallback((nextItems: T[]) => {
        pastRef.current = [];
        futureRef.current = [];
        baselineRowsRef.current = Utils.deepCopy(nextItems);
        baselineSignatureRef.current = serverSignature(nextItems);
        loadedAllocationSignatureRef.current = allocationSignature(nextItems);
        bump();
    }, [bump]);

    /**
     * Restores server-backed fields from the last save and keeps the current session allocations.
     */
    const revert = useCallback((currentItems: T[]) => {
        if (serverSignature(currentItems) === baselineSignatureRef.current)
            return null;
        const restored = reattachAllocations(Utils.deepCopy(baselineRowsRef.current), currentItems);
        pastRef.current = [];
        futureRef.current = [];
        bump();
        return restored;
    }, [bump]);

    const status = useMemo(() => {
        void version;
        return {
            canUndo: pastRef.current.length > 0,
            canRedo: futureRef.current.length > 0,
            changes: pastRef.current.map(entry => entry.change),
            isServerDirty: serverSignature(items) !== baselineSignatureRef.current,
            isAllocationDirty: allocationSignature(items) !== loadedAllocationSignatureRef.current,
        };
    }, [items, version]);

    return {
        ...status,
        commit,
        undo,
        redo,
        revert,
        reset,
        clearHistory,
        setServerBaseline,
        readBaseline,
        acknowledgeSavedDetails,
        dismissCommittedDetails,
    };
};
